import ReplayIcon from '@mui/icons-material/Replay';
import { Grid, Paper, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { UseFormReturn } from 'react-hook-form';

import { gridSize, gridSizeMdLg5, gridSizeMdLg6 } from '@/shared';
import { CacheKeysPreventaEnum } from '@/shared/actions/erp';
import { SingleIconButton } from '@/shared/components/ui';
import { AlertSeverityEnum, IDataPreventa } from '@/shared/interfaces';
import { useAuthStore } from '@/store/auth';
import { useInstallScheduleStore } from '@/store/erp/planeacion';
import { useUiStore } from '@/store/ui';
import { useInstallSchedule } from '../../hooks/useInstallSchedule';
import CustomInstallSchedulePaper from './CustomInstallSchedulePaper';

export type TimerAndHourInstallScheduleComponentProps = {
  minutes: number | string;
  seconds: number | string;
  setIsOpenModal: (isOpen: boolean) => void;
  form: UseFormReturn<any>;
  watchedFlota?: string;
  preventa: IDataPreventa;
  cackeKey?: CacheKeysPreventaEnum;
};

const TimerAndHourInstallScheduleComponent: React.FC<
  TimerAndHourInstallScheduleComponentProps
> = ({
  form,
  watchedFlota,
  preventa,
  minutes,
  seconds,
  setIsOpenModal,
  cackeKey = CacheKeysPreventaEnum.HORARIO_INSTALACION_PREAGENDA_BASE,
}) => {
  ///* hooks ============================
  const { getTimeMapsByFlota } = useInstallSchedule({
    form,
    preventa: preventa!,
    cackeKey,
  });

  ///* global state ============================
  const hasStartedTimer = useInstallScheduleStore(s => s.hasStartedTimer);
  const selectedHour = useInstallScheduleStore(s => s.selectedHour);
  const setSelectedHour = useInstallScheduleStore(s => s.setSelectedHour);
  const userId = useAuthStore(s => s.user?.id);
  const setAlert = useUiStore(s => s.setAlert);

  const timeMapPreagenda = useInstallScheduleStore(s => s.timeMap);
  const setTimeMapPreagenda = useInstallScheduleStore(s => s.setTimeMap);
  const cacheHorarioData = useInstallScheduleStore(s => s.cacheHorarioData);

  ///* handlers ============================
  const onRefreshTimeMaps = async () => {
    await getTimeMapsByFlota(
      cacheHorarioData?.flotaCode || watchedFlota! || ''
    );

    const allTimeMaps = useInstallScheduleStore.getState().allTimeMaps;
    const selectedHour =
      cacheHorarioData?.selectedHour ||
      useInstallScheduleStore.getState().selectedHour;
    const selectedDate =
      cacheHorarioData?.selectedDate ||
      useInstallScheduleStore.getState().selectedDate;

    const selectedTimeMap = allTimeMaps?.find(item => {
      return (
        item.fecha === cacheHorarioData?.selectedDate ||
        item.fecha === selectedDate
      );
    });
    if (!selectedTimeMap) return setTimeMapPreagenda(null);

    // filter previous hours -------
    selectedTimeMap.horas = selectedTimeMap.horas?.filter(horaTimeMap => {
      const hourTimeMap = +(horaTimeMap?.hora || 0);
      const isSelectedHour =
        +hourTimeMap === +(cacheHorarioData?.selectedHour || 0) ||
        +hourTimeMap === +(selectedHour || 0);
      const isSameUser = userId === cacheHorarioData?.userId;

      // Si es la hora seleccionada, sólo pasa si es el mismo usuario - cache | prevenir q se filtre x tener preventa cuando si es el mismo usuario
      if (isSelectedHour) {
        return cacheHorarioData ? isSameUser : true;
      }

      // // Si tiene preventa, no pasa
      // const hasPreventaId = !!horaTimeMap?.id_preventa;
      // if (hasPreventaId) return false;

      // si el timeMap tiene  `fecha_hora_limite`  y es mayor a la fecha actual, no pasa
      const hasFechaHoraLimite = !!horaTimeMap?.fecha_hora_limite;
      if (hasFechaHoraLimite) {
        const fechaHoraLimite = dayjs(horaTimeMap?.fecha_hora_limite);
        const currentDate = dayjs();
        if (fechaHoraLimite.isAfter(currentDate)) return false;
      }

      return true;
    });

    setTimeMapPreagenda([selectedTimeMap]);
    setSelectedHour(
      cacheHorarioData?.selectedHour?.toString() ||
        selectedHour?.toString() ||
        null
    );
  };

  return (
    <Grid
      item
      xs={12}
      container
      spacing={3}
      justifyContent="center"
      alignItems="start"
      mt={2}
    >
      <Grid item {...gridSizeMdLg6}>
        <img
          src="/calendar-planificador.svg"
          alt="select hour"
          draggable="false"
          style={{
            width: '100%',
            height: 'auto',
            margin: 'auto',
            objectFit: 'cover',
          }}
        />
      </Grid>
      <span className="spacer" />

      <Grid
        item
        {...gridSizeMdLg5}
        container
        justifyContent="center"
        alignItems="center"
      >
        <>
          <Grid item container xs={12} spacing={2}>
            {/* --------- timer --------- */}
            {hasStartedTimer && (
              <Grid
                item
                {...gridSize}
                container
                spacing={1}
                alignItems="center"
              >
                <Grid item xs={12} md={10}>
                  <Typography variant="body1" sx={{ textAlign: 'start' }}>
                    Tiempo restante:{' '}
                    <strong>
                      {minutes}:{seconds}
                    </strong>{' '}
                    minutos
                  </Typography>
                </Grid>

                <Grid item xs={12} md={2}>
                  <SingleIconButton
                    startIcon={<ReplayIcon />}
                    onClick={() => {
                      onRefreshTimeMaps();
                    }}
                  />
                </Grid>
              </Grid>
            )}

            {/* --------- hours --------- */}
            <Grid item {...gridSize}>
              <Paper
                sx={{
                  maxHeight: 250,
                  overflowY: 'auto',
                  margin: 'auto',
                  textAlign: 'center',
                }}
              >
                {timeMapPreagenda?.at(0)?.horas?.map(timeHour => (
                  <CustomInstallSchedulePaper
                    key={timeHour?.hora}
                    hour={timeHour?.hora?.toString() || ''}
                    onClick={hour => {
                      if (selectedHour && hasStartedTimer) {
                        setAlert(
                          true,
                          'Ya seleccionaste un horario, espera a que termine el tiempo de bloqueo.',
                          AlertSeverityEnum.warning
                        );
                        return;
                      }

                      setSelectedHour(hour);
                      setIsOpenModal(true);
                    }}
                    isClicked={
                      selectedHour?.toString() === timeHour?.hora?.toString()
                    }
                  >
                    {timeHour && timeHour?.hora
                      ? dayjs().hour(timeHour?.hora).minute(0).format('HH:mm')
                      : 0}
                  </CustomInstallSchedulePaper>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </>
      </Grid>
    </Grid>
  );
};

export default TimerAndHourInstallScheduleComponent;
